import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { getAppConfig } from './config';
import { getClientSessionId } from '../utils/clientSession';
import { apiFetch } from '../utils/api';

type JwtState = {
  accessToken: string | null;
  expiresAtMs: number;
  inflight: Promise<string | null> | null;
};

export type SupabaseRuntime = {
  client: SupabaseClient;
  supabaseReady: Promise<SupabaseClient>;
  waitForJwt: (opts?: { timeoutMs?: number }) => Promise<boolean>;
  getAccessToken: () => string | null;
};

function getStartParam(): string | null {
  const p = (window as any).Telegram?.WebApp?.initDataUnsafe?.start_param;
  if (p && typeof p === 'string' && p.length > 0) return p;
  return null;
}

function getTelegramInitDataForJwt(): string | null {
  const sdkInitData = (window as any).Telegram?.WebApp?.initData;
  if (sdkInitData && typeof sdkInitData === 'string' && sdkInitData.length > 0) return sdkInitData;

  try {
    const cachedInitData = localStorage.getItem('telegramInitDataCache');
    if (cachedInitData && cachedInitData.length > 0) return cachedInitData;
  } catch {}

  try {
    const cachedLaunch = localStorage.getItem('telegramLaunchParams');
    if (cachedLaunch && cachedLaunch.length > 0) return decodeURIComponent(cachedLaunch);
  } catch {}

  return null;
}

function clearUserLocalStateOnScopeChange(prevUserUuid: string, nextUserUuid: string) {
  try {
    // Age wall
    localStorage.removeItem('aicams_age_verified');

    // Experiments (sticky assignments are per-user)
    localStorage.removeItem('aicams_exp_assignments_v1');
    localStorage.removeItem('aicams_exp_context_v1');
    localStorage.removeItem('aicams_exp_config_cache_v1');

    // Chat state + purchases (model-scoped)
    const prefixes = ['chat_state_model_', 'chat_purchased_model_'];
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (prefixes.some((p) => k.startsWith(p))) {
        localStorage.removeItem(k);
      }
    }

    // Notify app shell to re-check gates (best-effort)
    try {
      window.dispatchEvent(new CustomEvent('aicams-user-scope-changed', { detail: { prevUserUuid, nextUserUuid } }));
    } catch {}
  } catch {}
}

function applyUserUuidScope(userUuid: string | null) {
  const next = String(userUuid || '').trim();
  if (!next) return;
  try {
    const prev = String(localStorage.getItem('aicams_user_uuid') || '').trim();
    if (prev && prev !== next) clearUserLocalStateOnScopeChange(prev, next);
    localStorage.setItem('aicams_user_uuid', next);
  } catch {}
}

export function createSupabaseRuntime(): SupabaseRuntime {
  const { config } = getAppConfig();
  const apiBaseUrl = config.apiBaseUrl;
  const supabaseKey = String(config.supabase.publishableKey || '').trim();

  if (!supabaseKey) {
    throw new Error('Missing Supabase publishable key in runtime APP_CONFIG');
  }

  const jwt: JwtState = {
    accessToken: null,
    expiresAtMs: 0,
    inflight: null,
  };

  let resolveReady: (c: SupabaseClient) => void = () => {};
  const supabaseReady = new Promise<SupabaseClient>((resolve) => {
    resolveReady = resolve;
  });

  function setToken(accessToken: string | null, expiresInSec?: number) {
    jwt.accessToken = accessToken;
    jwt.expiresAtMs = accessToken ? Date.now() + ((expiresInSec || 3600) * 1000) - 60_000 : 0;
  }

  async function fetchSessionJwt(): Promise<string | null> {
    const startParam = getStartParam();
    if (!startParam || !startParam.startsWith('session_')) return null;

    try {
      const clientSessionId = getClientSessionId()
      const res = await apiFetch(`${apiBaseUrl}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: startParam,
          client_session_id: clientSessionId,
          platform: (window as any)?.Telegram?.WebApp?.platform || null,
          app_version: (() => { try { return (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) ? String(__APP_VERSION__) : null } catch { return null } })(),
          url: (() => { try { return String(window.location.href || '') } catch { return null } })(),
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.access_token) {
        applyUserUuidScope(data?.user?.user_uuid ?? null);
        setToken(data.access_token, data.expires_in);
        return data.access_token;
      }
    } catch {}
    return null;
  }

  async function fetchTelegramJwt(): Promise<string | null> {
    // 0) Prefer session-token auth
    const sessionJwt = await fetchSessionJwt();
    if (sessionJwt) {
      return sessionJwt;
    }

    const initData = getTelegramInitDataForJwt();
    if (!initData) return null;

    try {
      const clientSessionId = getClientSessionId()
      const res = await apiFetch(`${apiBaseUrl}/api/auth/telegram`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initData,
          client_session_id: clientSessionId,
          platform: (window as any)?.Telegram?.WebApp?.platform || null,
          app_version: (() => { try { return (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) ? String(__APP_VERSION__) : null } catch { return null } })(),
          url: (() => { try { return String(window.location.href || '') } catch { return null } })(),
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.access_token) {
        applyUserUuidScope(data?.user?.user_uuid ?? null);
        setToken(data.access_token, data.expires_in);
        return data.access_token;
      }
    } catch {}
    return null;
  }

  async function ensureValidToken(): Promise<string | null> {
    if (jwt.accessToken && Date.now() < jwt.expiresAtMs) return jwt.accessToken;
    if (jwt.inflight) return jwt.inflight;

    jwt.inflight = (async () => {
      const tok = await fetchTelegramJwt();
      return tok;
    })().finally(() => {
      jwt.inflight = null;
    });

    return jwt.inflight;
  }

  async function waitForJwt(opts: { timeoutMs?: number } = {}): Promise<boolean> {
    const timeoutMs = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : 3000;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const tok = await ensureValidToken();
      if (tok) return true;
      await new Promise((r) => setTimeout(r, 150));
    }
    return false;
  }

  const client: SupabaseClient = createClient(config.supabase.url, supabaseKey, {
    accessToken: async () => await ensureValidToken(),
    auth: {
      autoRefreshToken: false,
      detectSessionInUrl: false,
      persistSession: false,
    },
  });

  // auto-init: resolveReady immediately with current client and try to populate token in background
  resolveReady(client);
  setTimeout(() => {
    ensureValidToken().finally(() => {
      // keep same ready; accessToken() reads current token state lazily
    });
  }, 0);

  return {
    get client() {
      return client;
    },
    supabaseReady,
    waitForJwt,
    getAccessToken: () => jwt.accessToken,
  };
}
